import CopyLinkButton from '@/components/Discord/CopyLinkButton'
import { useState } from 'react'
import GuidelineTooltip from '../../../components/Discord/GuidelineTooltip'
import styles from '@/components/Discord/DiscordDocument.module.css'
import DiscordDocument from '@/components/Discord/DiscordDocument'

function DiscordSexualSolicitationPolicyPage() {
  const [language, setLanguage] = useState<'en' | 'cs'>('cs')

  return (
    <DiscordDocument
      language={language}
      onLanguageChange={setLanguage}
      heading={
        <div className={styles.titleSection}>
          <h1 className={styles.title}>
            {language === 'cs' ? 'Politika sexuálního navádění' : 'Sexual Solicitation Policy'}
          </h1>
        </div>
      }
      notice={
        <>
          <div className={styles.disclaimer}>
            <div className={styles.disclaimerCard} lang="cs">
              <h3>Důležité upozornění</h3>
              <p>
                Nejsme přidruženi ani spojeni s Discord Inc. Toto je pouze komunitní překlad
                oficiální stránky o politice sexuálního navádění Discord dostupné na{' '}
                <a
                  href="https://discord.com/safety/sexual-solicitation-policy-explainer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  discord.com/safety/sexual-solicitation-policy-explainer
                </a>
                .
              </p>
              <p>
                Tento překlad nemusí být aktuální s oficiální politikou. Pokud si všimnete rozdílu
                mezi oficiální stránkou a touto stránkou, prosím, dejte nám vědět na našem Discord
                serveru.
              </p>
            </div>
            <div className={styles.disclaimerCard} lang="en">
              <h3>Important Notice</h3>
              <p>
                We are not affiliated with or associated with Discord Inc. This is only a community
                translation of the official Discord sexual solicitation policy available at{' '}
                <a
                  href="https://discord.com/safety/sexual-solicitation-policy-explainer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  discord.com/safety/sexual-solicitation-policy-explainer
                </a>
                .
              </p>
              <p>
                This translation may not be up to date with the official policy. If you notice a
                difference between the official page and this page, please let us know on our
                Discord server.
              </p>
            </div>
          </div>
        </>
      }
    >
      {/* Quick Summary - Czech only */}
      {language === 'cs' && (
        <details className={styles.quickSummary} id="quick-summary">
          <summary>
            <h2>Rychlé shrnutí politiky</h2>
          </summary>
          <CopyLinkButton
            sectionId={'quick-summary'}
            language={language}
            className={styles.copyBtn}
          />

          <div className={styles.summaryGrid}>
            <div className={styles.summaryCard}>
              <h3>Zakázané činnosti</h3>
              <ul>
                <li>Žádné nabídky osobních sexuálních služeb za úplatu</li>
                <li>Zákaz reklamy na eskortní služby</li>
                <li>Žádné koordinování sexuálních schůzek</li>
                <li>Zákaz serverů pro spojování sex workerů s klienty</li>
              </ul>
            </div>
            <div className={styles.summaryCard}>
              <h3>Důležité upozornění</h3>
              <ul>
                <li>Politika se vztahuje pouze na dospělé osoby</li>
                <li>Sexuální navádění dětí spadá pod jiné politiky</li>
                <li>Nekoordinujte platby za osobní sexuální služby</li>
                <li>Okamžitě nahlaste porušení této politiky</li>
              </ul>
            </div>
            <div className={styles.summaryCard}>
              <h3>Jak se chránit</h3>
              <ul>
                <li>Neúčastněte se žádného sexuálního navádění</li>
                <li>Pokud si nejste jisti, obsah raději nesdílejte</li>
                <li>Blokujte uživatele porušující tuto politiku</li>
                <li>Opusťte servery věnované těmto aktivitám</li>
              </ul>
            </div>
          </div>
          <p className={styles.summaryNote}>
            Toto je pouze stručné shrnutí. Pro úplné znění čtěte celý dokument níže.
          </p>
        </details>
      )}

      {/* Policy Body */}
      <div className={styles.policyBody}>
        {language === 'cs' ? (
          <>
            <section id="sexual-solicitation" className={styles.policySection}>
              <CopyLinkButton
                sectionId={'sexual-solicitation'}
                language={language}
                className={styles.copyBtn}
              />
              <h2>
                <strong>Sexuální navádění</strong>
              </h2>
              <p>
                Uživatelé starší 18 let mohou na Discordu sdílet sexuální obsah a přistupovat k němu
                podle příslušných pravidel. Nabízení či poptávání osobních sexuálních služeb za
                úplatu však zakazujeme. Sexuální navádění zahrnuje žádosti od lidí, kteří hledají
                nebo nabízejí osobní sexuální služby výměnou za odměnu. To zahrnuje činnosti, které
                by usnadnily nebo podporovaly osobní sexuální setkání, jako je reklama nebo
                propagace sexuálních služeb, stejně jako koordinace logistiky nebo plateb za osobní
                sexuální služby.
              </p>
              <p>
                Tato politika pokrývá chování mezi dospělými. Jakékoli případy sexuálního navádění
                dospělých vůči dětem budou posuzovány podle našich{' '}
                <a
                  href="https://discord.com/safety/child-safety-policy-explainer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  politik bezpečnosti dospívajících a dětí
                </a>
                .
              </p>
            </section>

            <section id="how-to-avoid" className={styles.policySection}>
              <CopyLinkButton
                sectionId={'how-to-avoid'}
                language={language}
                className={styles.copyBtn}
              />
              <h2>
                <strong>Jak se vyhnout porušení této politiky</strong>
              </h2>
              <ul>
                <li>
                  Nezapojujte se do činností podporujících nebo koordinujících sexuální navádění
                </li>
                <li>
                  Pokud si nejste jisti, zda by část obsahu mohla porušit tuto politiku,
                  nezveřejňujte ji ani se do ní nezapojujte
                </li>
              </ul>
              <p>
                Pokud vidíte tento obsah sdílený v přímé zprávě nebo na serveru,{' '}
                <a
                  href="/discord/safety/reporting-abusive-behavior-to-discord"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  nahlaste
                </a>{' '}
                nám to okamžitě, zablokujte uživatele a opusťte DM nebo server.
              </p>
            </section>

            <section id="how-to-report" className={styles.policySection}>
              <CopyLinkButton
                sectionId={'how-to-report'}
                language={language}
                className={styles.copyBtn}
              />
              <h2>
                <strong>Jak nahlásit porušení této politiky</strong>
              </h2>
              <p>
                Nahlašování porušení bezpečnosti je kriticky důležité pro udržení bezpečnosti vás a
                širší komunity Discord. Všichni uživatelé Discordu mohou nahlásit porušení politik v
                aplikaci podle pokynů{' '}
                <a
                  href="/discord/safety/reporting-abusive-behavior-to-discord"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  zde
                </a>
                . Pro více informací o této politice se prosím podívejte na naše{' '}
                <a href="/discord/guidelines">Pokyny komunity</a>{' '}
                <GuidelineTooltip guidelineNumber="27" language={language}>
                  <a href="/discord/guidelines#guideline-27" className={styles.guidelineLink}>
                    #27
                  </a>
                </GuidelineTooltip>
                .
              </p>
            </section>
          </>
        ) : (
          <>
            <section id="sexual-solicitation" className={styles.policySection}>
              <CopyLinkButton
                sectionId={'sexual-solicitation'}
                language={language}
                className={styles.copyBtn}
              />
              <h2>
                <strong>Sexual Solicitation</strong>
              </h2>
              <p>
                Adults over 18 may share and access sexual content on Discord under the applicable
                rules. Paid in-person sexual solicitation remains prohibited. Sexual solicitation
                involves requests from people looking for or offering in-person sexual services in
                exchange for compensation. This includes activities that would facilitate or
                encourage in-person sexual meetups, such as advertising or promoting sexual
                services, as well as coordinating logistics or payments for in-person sexual
                services.
              </p>
              <p>
                This policy covers adult-to-adult conduct. Any instances of adult-to-child sexual
                solicitation will be reviewed under our{' '}
                <a
                  href="https://discord.com/safety/child-safety-policy-explainer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Teen and Child Safety Policies
                </a>
                .
              </p>
            </section>

            <section id="how-to-avoid" className={styles.policySection}>
              <CopyLinkButton
                sectionId={'how-to-avoid'}
                language={language}
                className={styles.copyBtn}
              />
              <h2>
                <strong>How to Avoid Violating This Policy</strong>
              </h2>
              <ul>
                <li>Do not engage in acts supporting or coordinating sexual solicitation</li>
                <li>
                  If you aren't sure if a piece of content might violate this policy, do not post it
                  or engage with it
                </li>
              </ul>
              <p>
                If you see this content being shared in a direct message or in a server,{' '}
                <a
                  href="/discord/safety/reporting-abusive-behavior-to-discord"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  report
                </a>{' '}
                it to us immediately, block the user, and leave the DM or server.
              </p>
            </section>

            <section id="how-to-report" className={styles.policySection}>
              <CopyLinkButton
                sectionId={'how-to-report'}
                language={language}
                className={styles.copyBtn}
              />
              <h2>
                <strong>How to Report Violations of This Policy</strong>
              </h2>
              <p>
                Reporting safety violations is critically important to keeping you and the broader
                Discord community safe. All Discord users can report policy violations in the app by
                following the instructions{' '}
                <a
                  href="/discord/safety/reporting-abusive-behavior-to-discord"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </a>
                . For more information on this policy, please reference our{' '}
                <a href="/discord/guidelines">Community Guidelines</a>{' '}
                <GuidelineTooltip guidelineNumber="27" language={language}>
                  <a href="/discord/guidelines#guideline-27" className={styles.guidelineLink}>
                    #27
                  </a>
                </GuidelineTooltip>
                .
              </p>
            </section>
          </>
        )}
      </div>

      {/* Tags section */}
      <div className={styles.tags}>
        <div className={styles.tagsLabel}>Tags</div>
        <div className={styles.tagsList}>
          <div className={styles.tag}>
            {language === 'cs'
              ? 'Regulované nebo nelegální činnosti'
              : 'Regulated or Illegal Activities'}
          </div>
        </div>
      </div>
    </DiscordDocument>
  )
}

export default DiscordSexualSolicitationPolicyPage
